import React from 'react';
import { X } from 'lucide-react';

const ReviewChangesModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  currentPlan, 
  newPlan, 
  changes,
  pricing,
  billingInfo,
  loading 
}) => {
  if (!isOpen) return null;

  const calculateNewTotal = () => {
    // Start with CURRENT total (not plan price)
    let total = pricing.currentTotal || 0;
    
    // Add new addons with QUANTITY support
    changes.addonsToAdd.forEach(addon => {
      const addonPrice = pricing.addonPrices[addon.id] || 0;
      const quantity = addon.quantity || 1;
      const lineTotal = addonPrice * quantity;
      total += lineTotal;
      console.log(`Adding ${addon.name} x${quantity}: +$${lineTotal} ($${addonPrice} each), new total: $${total}`);
    });
    
    // Subtract removed addons with QUANTITY support
    changes.addonsToRemove.forEach(addon => {
      const addonPrice = pricing.addonPrices[addon.id] || 0;
      const quantity = addon.quantity || 1;
      const lineTotal = addonPrice * quantity;
      total -= lineTotal;
      console.log(`Removing ${addon.name} x${quantity}: -$${lineTotal} ($${addonPrice} each), new total: $${total}`);
    });
    
    return total;
  };

  const newTotal = calculateNewTotal();
  const priceDifference = newTotal - (pricing.currentTotal || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Review Subscription Changes</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Plan */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Current Plan</h3>
            <div className="text-lg font-semibold text-gray-900">
              {currentPlan.name}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              ${pricing.currentTotal || 0}/year
            </div>
            {currentPlan.addons && currentPlan.addons.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                Active Add-ons: {currentPlan.addons.map(a => a.name).join(', ')}
              </div>
            )}
          </div>

          {/* Changes Summary */}
          {(changes.planChange || changes.addonsToAdd.length > 0 || changes.addonsToRemove.length > 0) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Changes:</h3>
              
              {/* Plan Change */}
              {changes.planChange && (
                <div className="flex items-center space-x-2 text-sm">
                  <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800">
                    Plan Change
                  </span>
                  <span className="text-gray-600">
                    {currentPlan.name} → {newPlan.name}
                  </span>
                </div>
              )}

              {/* Add-ons Being Added */}
              {changes.addonsToAdd.map((addon, idx) => (
                <div key={`add-${idx}`} className="flex items-center space-x-2 text-sm">
                  <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800">
                    Enable Add-on
                  </span>
                  <span className="text-gray-600">{addon.name}</span>
                  {addon.quantity > 1 && (
                    <span className="text-gray-500">(Qty: {addon.quantity})</span>
                  )}
                </div>
              ))}

              {/* Add-ons Being Removed */}
              {changes.addonsToRemove.map((addon, idx) => (
                <div key={`remove-${idx}`} className="flex items-center space-x-2 text-sm">
                  <span className="inline-flex items-center px-2 py-1 rounded bg-red-100 text-red-800">
                    Remove Add-on
                  </span>
                  <span className="text-gray-600">{addon.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* New Plan Summary */}
          <div className="bg-indigo-50 border-2 border-indigo-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-indigo-700 mb-2">New Plan & Pricing</h3>
            <div className="text-lg font-semibold text-indigo-900">
              {newPlan.name}
            </div>
            <div className="text-2xl font-bold text-indigo-600 mt-1">
              ${newTotal}/year
            </div>
            {priceDifference !== 0 && (
              <div className={`text-sm mt-2 ${priceDifference > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {priceDifference > 0 ? '+' : ''}${priceDifference} from current plan
              </div>
            )}
          </div>

          {/* Billing Information */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Billing Information</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Frequency:</span>
                <span className="font-medium">{billingInfo.frequency || 'Yearly'}</span>
              </div>
              {billingInfo.nextBillingDate && (
                <div className="flex justify-between">
                  <span>Next Billing Date:</span>
                  <span className="font-medium">{billingInfo.nextBillingDate}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Effective:</span>
                <span className="font-medium">Immediately after payment</span>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Important</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>You'll be redirected to a secure Stripe checkout page to confirm this subscription change and process payment. Your changes will only take effect after successful payment.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Confirm and Proceed to Secure Checkout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewChangesModal;
